import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { User, UserTier, UserStatus, Match, formatCurrency, MIN_TICKET_PRICE, validateFightPercentage, validateTicketPrice, getFightPercentageRange, getTicketPriceRange } from '@/lib/index';
import { useAuth } from '@/hooks/useAuth';
import { useLanguage } from '@/lib/i18n';
import { useUserManagement } from '@/hooks/useUserManagement';
import { useBetting } from '@/hooks/useBetting';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { MoreHorizontal, UserPlus, ShieldAlert, ShieldOff, Lock, Wallet, Percent, Ticket, Search, CreditCard, ChevronRight, ChevronDown, Users, Building2, UserCheck, TrendingUp, LayoutList, Save, X as XIcon, ChevronLeft, Tag, RefreshCw, Clock, Calendar, Pencil, Check, Eye, EyeOff, Plus, Minus } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';

/**
 * Generate fight% option values: 0% to max in 1% increments.
 */
function getIndividualFightPctOptions(max: number): number[] {
  const options: number[] = [];
  for (let v = 0; v <= max; v += 1) options.push(v);
  return options;
}

export function UserManagementPanel() {
  const {
    user,
    isMaster,
    isCompany,
    isAgent,
    patchUser
  } = useAuth();
  const {
    users,
    isLoading,
    error,
    loadUsers,
    createUser,
    updateUser,
    changeStatus,
    getFightSetting,
    getBlanketFightSetting,
    setFightSetting,
    setFightBlanket,
  } = useUserManagement();
  const [searchQuery, setSearchQuery] = useState('');
  const [tierFilter, setTierFilter] = useState<'ALL' | 'AGENT' | 'PLAYER'>('ALL');
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  // Sub-view routing inside UserManagement
  const [view, setView] = useState<'table' | 'create' | 'edit'>('table');
  const [editTargetUser, setEditTargetUser] = useState<User | null>(null);

  const openCreatePage = () => setView('create');
  const openEditPage = (u: User) => { setEditTargetUser(u); setView('edit'); };
  const returnToTable = () => { setView('table'); setEditTargetUser(null); };

  // ── Inline description edit state ─────────────────────────────────────────
  // editingDescriptionId: which row is currently in edit mode
  // descriptionDraft:     current typed value for that row
  const [editingDescriptionId, setEditingDescriptionId] = useState<string | null>(null);
  const [descriptionDraft, setDescriptionDraft] = useState('');
  const [savingDescriptionId, setSavingDescriptionId] = useState<string | null>(null);

  const startEditDescription = (userId: string, current: string | undefined) => {
    setEditingDescriptionId(userId);
    setDescriptionDraft(current ?? '');
  };

  const cancelEditDescription = () => {
    setEditingDescriptionId(null);
    setDescriptionDraft('');
  };

  const saveDescription = async (userId: string) => {
    setSavingDescriptionId(userId);
    try {
      await updateUser(userId, { description: descriptionDraft.trim() || '' });
      toast.success('Description saved');
      setEditingDescriptionId(null);
      setDescriptionDraft('');
    } catch {
      toast.error('Failed to save description');
    } finally {
      setSavingDescriptionId(null);
    }
  };

  // Default: only expand the current user's own row; children are collapsed
  useEffect(() => {
    if (user && users.length > 0) {
      setExpandedNodes(new Set([user.id]));
    }
  }, [user?.id, users.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Silently refresh the user list every 30 s so ban/status changes from auth are reflected
  useEffect(() => {
    const interval = setInterval(() => loadUsers(true), 30000);
    return () => clearInterval(interval);
  }, [loadUsers]);

  // Toggle expand/collapse for a user node
  const toggleExpanded = (userId: string) => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(userId)) {
      newExpanded.delete(userId);
    } else {
      newExpanded.add(userId);
    }
    setExpandedNodes(newExpanded);
  };

  // Build hierarchical user tree
  const buildUserTree = (parentId: string | null = null, level: number = 0): User[] => {
    return users.filter(u => u.parentId === parentId).sort((a, b) => {
      // Sort by tier first (COMPANY, AGENT, PLAYER), then by username
      const tierOrder = {
        COMPANY: 0,
        AGENT: 1,
        PLAYER: 2
      };
      const tierDiff = tierOrder[a.tier as keyof typeof tierOrder] - tierOrder[b.tier as keyof typeof tierOrder];
      return tierDiff !== 0 ? tierDiff : a.username.localeCompare(b.username);
    });
  };

  // Get all visible users in tree format
  const getVisibleUsers = (): Array<User & {
    level: number;
    hasChildren: boolean;
    isExpanded: boolean;
  }> => {
    const result: Array<User & {
      level: number;
      hasChildren: boolean;
      isExpanded: boolean;
    }> = [];
    const addUsersRecursively = (parentId: string | null, level: number) => {
      const children = buildUserTree(parentId, level);
      for (const child of children) {
        const hasChildren = users.some(u => u.parentId === child.id);
        const isExpanded = expandedNodes.has(child.id);

        // Apply search filter
        if (!searchQuery || child.username.toLowerCase().includes(searchQuery.toLowerCase())) {
          result.push({
            ...child,
            level,
            hasChildren,
            isExpanded
          });
        }

        // Add children if expanded (and not filtered out by search)
        if (isExpanded && (!searchQuery || child.username.toLowerCase().includes(searchQuery.toLowerCase()))) {
          addUsersRecursively(child.id, level + 1);
        }
      }
    };

    // Start with top-level users (companies)
    addUsersRecursively(null, 0);
    return result;
  };

  // Get hierarchical users based on current user permissions
  const hierarchicalUsers = useMemo(() => {
    if (!user) return [];

    // Filter users based on permissions first
    let allowedUsers: User[] = [];
    let hierarchyStartId: string | null = null;
    if (isMaster()) {
      // Master sees ALL users including all Company accounts
      allowedUsers = users;
      hierarchyStartId = null;
    } else if (isCompany() && !isMaster()) {
      // Company sees only their own tree (themselves + subordinates)
      const getSubordinates = (parentId: string): User[] => {
        const direct = users.filter(u => u.parentId === parentId);
        return direct.concat(direct.flatMap(d => getSubordinates(d.id)));
      };
      allowedUsers = [user, ...getSubordinates(user.id)];
      hierarchyStartId = user.id;
    } else if (isAgent()) {
      // Agent sees their own line (recursive search)
      const getSubordinates = (parentId: string): User[] => {
        const direct = users.filter(u => u.parentId === parentId);
        return direct.concat(direct.flatMap(d => getSubordinates(d.id)));
      };
      // Prefer the entry from the `users` DB list (kept fresh by onUpdate/loadUsers)
      // so that self-edits reflect immediately without needing an auth session refresh.
      const selfFromList = users.find(u => u.id === user.id);
      const selfRow = selfFromList ?? user;
      allowedUsers = [selfRow, ...getSubordinates(user.id)];
      hierarchyStartId = user.id; // Start from agent's own ID
    }

    // Build hierarchical structure from allowed users
    const buildHierarchy = (parentId: string | null, level: number): Array<User & {
      level: number;
      hasChildren: boolean;
      isExpanded: boolean;
    }> => {
      const result: Array<User & {
        level: number;
        hasChildren: boolean;
        isExpanded: boolean;
      }> = [];
      const children = allowedUsers.filter(u => u.parentId === parentId).sort((a, b) => {
        const tierOrder = {
          MASTER: 0,
          COMPANY: 1,
          AGENT: 2,
          PLAYER: 3
        };
        const tierDiff = tierOrder[a.tier as keyof typeof tierOrder] - tierOrder[b.tier as keyof typeof tierOrder];
        return tierDiff !== 0 ? tierDiff : a.username.localeCompare(b.username);
      });
      for (const child of children) {
        const hasChildren = allowedUsers.some(u => u.parentId === child.id);
        const isExpanded = expandedNodes.has(child.id);

        // Apply search filter
        if (!searchQuery || child.username.toLowerCase().includes(searchQuery.toLowerCase())) {
          result.push({
            ...child,
            level,
            hasChildren,
            isExpanded
          });

          // Add children if expanded
          if (isExpanded) {
            result.push(...buildHierarchy(child.id, level + 1));
          }
        }
      }
      return result;
    };

    // Helper: get direct downlines of viewer filtered by tier
    const getDirectDownlines = (parentId: string) => {
      const direct = allowedUsers.filter(u => u.parentId === parentId);
      return direct
        .filter(u => {
          if (tierFilter === 'AGENT') return u.tier === UserTier.AGENT;
          if (tierFilter === 'PLAYER') return u.tier === UserTier.PLAYER;
          return true;
        })
        .sort((a, b) => {
          const tierOrder = { MASTER: 0, COMPANY: 1, AGENT: 2, PLAYER: 3 };
          const td = tierOrder[a.tier as keyof typeof tierOrder] - tierOrder[b.tier as keyof typeof tierOrder];
          return td !== 0 ? td : a.username.localeCompare(b.username);
        })
        .map(child => ({
          ...child,
          level: 1,
          hasChildren: allowedUsers.some(u => u.parentId === child.id),
          isExpanded: expandedNodes.has(child.id)
        }));
    };

    // For Company (non-master): show themselves at top, then their children
    if (!isMaster() && isCompany() && user && hierarchyStartId === user.id) {
      const companyNode = allowedUsers.find(u => u.id === user.id);
      if (companyNode) {
        const hasChildren = allowedUsers.some(u => u.parentId === user.id);
        const isExpanded = expandedNodes.has(user.id);
        const companyWithMeta = { ...companyNode, level: 0, hasChildren, isExpanded };
        if (tierFilter !== 'ALL') {
          // Show viewer row + direct filtered downlines (flat, no further expansion)
          return [companyWithMeta, ...getDirectDownlines(user.id)];
        }
        if (isExpanded) return [companyWithMeta, ...buildHierarchy(user.id, 1)];
        return [companyWithMeta];
      }
    }
    // For agents, show themselves at the top, then their children
    if (isAgent() && user) {
      const agentNode = allowedUsers.find(u => u.id === user.id);
      if (agentNode) {
        const hasChildren = allowedUsers.some(u => u.parentId === user.id);
        const isExpanded = expandedNodes.has(user.id);
        const agentWithMeta = { ...agentNode, level: 0, hasChildren, isExpanded };
        if (tierFilter !== 'ALL') {
          // Show viewer row + direct filtered downlines (flat, no further expansion)
          return [agentWithMeta, ...getDirectDownlines(user.id)];
        }
        // Show children only if expanded
        if (isExpanded) {
          return [agentWithMeta, ...buildHierarchy(user.id, 1)];
        }
        return [agentWithMeta];
      }
    }
    const allRows = buildHierarchy(hierarchyStartId, 0);
    // Apply tier filter — when filtering, also include parent nodes so hierarchy makes sense
    if (tierFilter === 'ALL') return allRows;
    return allRows.filter(u => {
      if (tierFilter === 'AGENT') return u.tier === UserTier.AGENT || u.tier === UserTier.COMPANY || u.tier === UserTier.MASTER;
      if (tierFilter === 'PLAYER') return u.tier === UserTier.PLAYER;
      return true;
    });
  }, [users, user, isMaster, isCompany, isAgent, searchQuery, expandedNodes, tierFilter]);
  // Track which rows are saving fight pct inline
  const [savingFightPct, setSavingFightPct] = useState<Set<string>>(new Set());

  /** Handle the agent's own fight%-mode dropdown (single % or 'multiple'/'individual') */
  const handleOwnFightPctModeChange = async (newValue: string) => {
    if (!user) return;
    const mark = (id: string) => setSavingFightPct(prev => new Set(prev).add(id));
    const unmark = (id: string) => setSavingFightPct(prev => { const s = new Set(prev); s.delete(id); return s; });
    mark(user.id);
    try {
      if (newValue === 'multiple') {
        // Switch to Individual mode — reset all direct children's parentFightPct to 0 first
        const directChildren = users.filter(c => c.parentId === user.id);
        await Promise.all(directChildren.map(c => updateUser(c.id, { parentFightPct: 0 })));
        await updateUser(user.id, { fightPctMode: 'multiple' });
      } else {
        const newPct = parseInt(newValue);
        const myLimit = user.fightPctLimit ?? 0;
        // Blanket fight%: only capped by the user's own fight% limit
        if (newPct > myLimit) { toast.error(`Your fight % limit is ${myLimit}%`); return; }
        // Call fight_pct_mgmt to record history
        const { data } = await supabase.functions.invoke('fight_pct_mgmt_2026_05_19', {
          body: { action: 'set_fight_pct', targetUserId: user.id, changedBy: user.id, newPct, note: 'single_all' }
        });
        if (data?.error) { toast.error(data.error); return; }
        // Save the fight pct on own row (users table)
        await updateUser(user.id, { fightPctMode: 'single', fightPercentage: newPct });
        // Only propagate to children if blanket is currently ON
        // Use set_fight_blanket edge fn so it handles per-child deduction (blanket% - child's limit, min 0)
        const blanketOn = getBlanketFightSetting(user.id).fightOnOff;
        if (blanketOn) {
          await setFightBlanket(user.id, true, newPct);
          // Also update parentFightPct on each child row for reporting compatibility
          const directChildren = users.filter(c => c.parentId === user.id);
          await Promise.all(directChildren.map(async c => {
            const childLimit = c.fightPctLimit ?? 0;
            const isChildPlayer = c.tier === UserTier.PLAYER;
            // Cap rule: child agent can never show more than (parentLimit - childLimit)
            // regardless of what blanket% is set to.
            //   blanket% <= maxDiff  → show blanket% as-is
            //   blanket% > maxDiff   → show maxDiff (the difference)
            const ownLimit = user.fightPctLimit ?? 0;
            const maxDiff = Math.max(0, ownLimit - childLimit);
            const effectivePct = isChildPlayer
              ? newPct
              : Math.min(newPct, maxDiff);
            return updateUser(c.id, { parentFightPct: effectivePct });
          }));
        }
      }
      await loadUsers(false);
    } catch (e) {
      toast.error('Failed to update fight %');
    } finally {
      unmark(user.id);
    }
  };

  /** Handle per-child fight % when parent is in 'multiple' (individual) mode */
  const handleChildFightPctChange = async (childId: string, newPct: number) => {
    if (!user) return;
    const mark = (id: string) => setSavingFightPct(prev => new Set(prev).add(id));
    const unmark = (id: string) => setSavingFightPct(prev => { const s = new Set(prev); s.delete(id); return s; });
    mark(childId);
    try {
      const childUser = users.find(c => c.id === childId);
      const parentLimit = user.fightPctLimit ?? 0;
      const childLimit = childUser?.fightPctLimit ?? 0;
      const maxDiff = Math.max(0, parentLimit - childLimit);
      const effectivePct = Math.min(newPct, maxDiff);
      // Save fight% to fight_settings relationship table
      await setFightSetting(user.id, childId, true, effectivePct);
      // Also keep parentFightPct on the child row for reporting compatibility
      await updateUser(childId, { parentFightPct: effectivePct });
      // Log to history
      await supabase.functions.invoke('fight_pct_mgmt_2026_05_19', {
        body: { action: 'set_fight_pct_multiple', targetUserId: childId, changedBy: user.id, newPct },
      });
      await loadUsers(false);
    } catch {
      toast.error('Failed to update fight %');
    } finally {
      unmark(childId);
    }
  };

  // ── Fight On/Off Handler ──────────────────────────────────────────────────
  // fightOnOff here means: does THIS viewer (parent) have fight enabled against a specific child?
  // It is a per-relationship setting, completely independent of the child's own fight settings.
  const [savingFightOnOff, setSavingFightOnOff] = useState<Set<string>>(new Set());

  /**
   * Toggle the CURRENT USER's fight on/off.
   *
   * OWN ROW (blanket): childId === user.id
   *   → reads/writes the sentinel row (parent_id = child_id = user.id)
   *   → OFF also resets all individual child rows to off+0%
   *   → ON  only updates the sentinel; child rows stay unchanged
   *
   * CHILD ROW (individual): childId !== user.id
   *   → reads/writes only the parent→child relationship row
   *   → NEVER touches the blanket sentinel
   */
  const handleFightOnOff = async (childId: string, enabled: boolean) => {
    if (!user) return;
    const isBlanket = childId === user.id;
    const spinnerKey = isBlanket ? `blanket_${user.id}` : `${user.id}:${childId}`;
    setSavingFightOnOff(prev => new Set(prev).add(spinnerKey));
    try {
      if (isBlanket) {
        // Blanket ON:  children ON + 0%, own fight% reset to 0% — user sets manually
        // Blanket OFF: children OFF + 0%, own fight% reset to 0%
        await setFightBlanket(user.id, enabled);
        // Reset own fight% to 0 in users table so the dropdown shows 0%
        await updateUser(user.id, { fightPercentage: 0 });
        toast.success(enabled
          ? 'Fight enabled — Fight% starts at 0%, set it for each downline'
          : 'Fight disabled against all downlines (Fight% reset to 0%)');
      } else {
        // Individual: toggle parent's fight against this ONE child only
        // Does NOT affect the blanket sentinel
        await setFightSetting(user.id, childId, enabled);
        toast.success(enabled ? 'Fight enabled' : 'Fight disabled (Fight% reset to 0%)');
      }
      await loadUsers(true);
    } catch {
      toast.error('Failed to update Fight On/Off');
    } finally {
      setSavingFightOnOff(prev => { const s = new Set(prev); s.delete(spinnerKey); return s; });
    }
  };

  // ── Credit Refresh Rate Handlers ─────────────────────────────────────────
  const [savingRefreshRate, setSavingRefreshRate] = useState<Set<string>>(new Set());
  const [manualRefreshing, setManualRefreshing] = useState<Set<string>>(new Set());

  const handleSetRefreshRate = async (targetId: string, rate: 'auto_daily' | 'auto_weekly' | 'manual') => {
    if (!user) return;
    setSavingRefreshRate(prev => new Set(prev).add(targetId));
    try {
      const { data, error: fnErr } = await supabase.functions.invoke('credit_refresh_2026_05_29', {
        body: { action: 'set_refresh_rate', targetUserId: targetId, callerId: user.id, refreshRate: rate }
      });
      if (fnErr || !data?.success) { toast.error(data?.error || 'Failed to update refresh rate'); return; }
      toast.success('Credit refresh rate updated');
      await loadUsers(true);
    } catch {
      toast.error('Failed to update refresh rate');
    } finally {
      setSavingRefreshRate(prev => { const s = new Set(prev); s.delete(targetId); return s; });
    }
  };

  const handleManualRefresh = async (targetId: string, targetName: string) => {
    if (!user) return;
    setManualRefreshing(prev => new Set(prev).add(targetId));
    try {
      const { data, error: fnErr } = await supabase.functions.invoke('credit_refresh_2026_05_29', {
        body: { action: 'manual_refresh', targetUserId: targetId, callerId: user.id }
      });
      if (fnErr || !data?.success) { toast.error(data?.error || 'Failed to refresh credit'); return; }
      toast.success(`Credit refreshed for ${targetName}`);
      // Reload users so credit allocation column updates immediately
      await loadUsers(false);
    } catch {
      toast.error('Failed to refresh credit');
    } finally {
      setManualRefreshing(prev => { const s = new Set(prev); s.delete(targetId); return s; });
    }
  };

  const handleUpdateUser = async (userId: string, updates: any) => {
    try {
      await updateUser(userId, updates);
      toast.success('User updated successfully');
    } catch (error) {
      toast.error('Failed to update user');
    }
  };
  const handleCreateUser = async (userData: any) => {
    try {
      await createUser(userData);
      toast.success(`User ${userData.username} created successfully`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to create user');
    }
  };
  const toggleStatus = async (target: User, status: UserStatus) => {
    try {
      // Use dedicated changeStatus so failed_login_attempts is reset on ACTIVE
      // and the list is silently refreshed to pick up any DB-side auto-bans
      await changeStatus(target.id, status);
      toast.info(`Account status updated for ${target.username}.`);
    } catch (error) {
      toast.error('Failed to update user status');
    }
  };
  // Calculate credit statistics
  const creditStats = useMemo(() => {
    if (!user) return {
      totalCredit: 0,
      allocatedCredit: 0,
      availableCredit: 0
    };
    const allocatedCredit = users.filter(u => u.parentId === user.id).reduce((sum, u) => sum + (u.creditLimit || 0), 0);
    return {
      totalCredit: user.creditLimit || 0,
      allocatedCredit,
      availableCredit: (user.creditLimit || 0) - allocatedCredit
    };
  }, [user, users]);
  const { t } = useLanguage();

  // Render sub-views
  if (view === 'create') {
    return <CreateUserPage
      users={users}
      onCreated={async (userData) => { await handleCreateUser(userData); returnToTable(); }}
      onBack={returnToTable}
    />;
  }
  if (view === 'edit' && editTargetUser) {
    return <EditProfilePage
      targetUser={editTargetUser}
      onUpdate={async (userId, updates) => { await handleUpdateUser(userId, updates); returnToTable(); }}
      onStatusChange={toggleStatus}
      onBack={returnToTable}
    />;
  }

  return <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">{t.userMgmt.title}</h2>
          <p className="text-muted-foreground">{t.userMgmt.subtitle}</p>
        </div>
        {(isCompany() || isAgent()) && (
          <Button className="bg-primary hover:bg-primary/90" onClick={openCreatePage}>
            <UserPlus className="mr-2 h-4 w-4" />
            {t.userMgmt.createUser}
          </Button>
        )}
      </div>

      {/* Credit Summary Card */}
      

      {/* Detailed Credit Breakdown */}
      

      {/* Credit Used Section - Company Only */}
      {isCompany && <Card className="border-blue-500/20 bg-gradient-to-br from-blue-500/5 to-transparent">
          
          
        </Card>}

      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="relative flex-1 min-w-[160px] max-w-sm">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input placeholder={t.userMgmt.searchPlaceholder} className="pl-8" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Tier filter */}
          <div className="flex items-center gap-1 border rounded-md overflow-hidden text-xs">
            <button
              onClick={() => setTierFilter('ALL')}
              className={cn('px-3 py-1.5 transition-colors', tierFilter === 'ALL' ? 'bg-primary text-primary-foreground font-semibold' : 'hover:bg-muted text-muted-foreground')}
            >All</button>
            <button
              onClick={() => setTierFilter('AGENT')}
              className={cn('px-3 py-1.5 transition-colors border-l', tierFilter === 'AGENT' ? 'bg-blue-600 text-white font-semibold' : 'hover:bg-muted text-muted-foreground')}
            >Agents</button>
            <button
              onClick={() => setTierFilter('PLAYER')}
              className={cn('px-3 py-1.5 transition-colors border-l', tierFilter === 'PLAYER' ? 'bg-green-600 text-white font-semibold' : 'hover:bg-muted text-muted-foreground')}
            >Players</button>
          </div>
          <Button variant="outline" size="sm" onClick={() => setExpandedNodes(new Set(users.map(u => u.id)))} className="text-xs">
            {t.userMgmt.expandAll}
          </Button>
          <Button variant="outline" size="sm" onClick={() => setExpandedNodes(new Set())} className="text-xs">
            {t.userMgmt.collapseAll}
          </Button>
        </div>
      </div>

      {/* Loading State */}
      {isLoading && <div className="flex items-center justify-center py-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
            <p className="text-muted-foreground">{t.userMgmt.loadingUsers}</p>
          </div>
        </div>}

      {/* Error State */}
      {error && <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
          <p className="text-destructive font-medium">{t.userMgmt.errorLoading}</p>
          <p className="text-destructive/80 text-sm mt-1">{error}</p>
        </div>}

      {/* Users Table */}
      {!isLoading && !error && <div className="border rounded-xl bg-card overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="bg-amber-100 dark:bg-amber-950/50 border-r border-amber-300 dark:border-amber-800">{t.userMgmt.colUsername}</TableHead>
              <TableHead className="min-w-[160px] bg-amber-100 dark:bg-amber-950/50 border-r border-amber-300 dark:border-amber-800">Description</TableHead>
              <TableHead className="bg-amber-100 dark:bg-amber-950/50 border-r border-amber-300 dark:border-amber-800">{t.userMgmt.colNickname}</TableHead>
              <TableHead className="bg-orange-100 dark:bg-orange-950/50 border-r border-orange-300 dark:border-orange-800">{t.userMgmt.colTier}</TableHead>
              <TableHead className="bg-orange-100 dark:bg-orange-950/50 border-r border-orange-300 dark:border-orange-800">{t.userMgmt.colCommission}</TableHead>
              <TableHead className="bg-blue-100 dark:bg-blue-950/50 border-r border-blue-300 dark:border-blue-800">{t.userMgmt.colFightOnOff}</TableHead>
              <TableHead className="bg-blue-100 dark:bg-blue-950/50 border-r border-blue-300 dark:border-blue-800">{t.userMgmt.colFightPct}</TableHead>
              <TableHead className="bg-blue-100 dark:bg-blue-950/50 border-r border-blue-300 dark:border-blue-800">{t.userMgmt.fightPctLimitLabel}</TableHead>
              <TableHead className="bg-green-100 dark:bg-green-950/50 border-r border-green-300 dark:border-green-800">{t.userMgmt.colCreditAlloc}</TableHead>
              <TableHead className="bg-green-100 dark:bg-green-950/50 border-r border-green-300 dark:border-green-800">Credit Refresh Rate</TableHead>
              <TableHead className="text-right bg-slate-100 dark:bg-slate-800/50 border-r border-slate-300 dark:border-slate-700">{t.userMgmt.colActions}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {hierarchicalUsers.length === 0 ? <TableRow>
                <TableCell colSpan={11} className="h-24 text-center text-muted-foreground">
                  {t.userMgmt.noUsers}
                </TableCell>
              </TableRow> : hierarchicalUsers.map(u => <TableRow key={u.id} className={cn(
                u.id === user?.id
                  ? 'bg-purple-300 dark:bg-purple-800/70 font-semibold'
                  : u.tier === UserTier.AGENT
                    ? 'bg-purple-50 dark:bg-purple-900/25'
                    : u.tier === UserTier.PLAYER
                      ? 'bg-yellow-50 dark:bg-yellow-900/20'
                      : 'bg-background'
              )}>
                  <TableCell className="font-medium border-r border-amber-200 dark:border-amber-800/60">
                    <div className="flex items-center gap-2" style={{
                paddingLeft: `${u.level * 48}px`
              }}>
                      {u.hasChildren ? <Button variant="ghost" size="sm" className="h-6 w-6 p-0 hover:bg-muted border border-muted-foreground/30" onClick={() => toggleExpanded(u.id)}>
                          {u.isExpanded ? <Minus className="h-3 w-3" /> : <Plus className="h-3 w-3" />}
                        </Button> : <div className="w-6 h-6 flex items-center justify-center">
                          <div className="w-2 h-2 rounded-full bg-muted-foreground/30"></div>
                        </div>}
                      
                      {/* User Icon based on tier */}
                      <div className="flex items-center gap-2">
                        {u.tier === UserTier.MASTER ? <Building2 className="h-4 w-4 text-purple-600" /> : u.tier === UserTier.COMPANY ? <Building2 className="h-4 w-4 text-primary" /> : u.tier === UserTier.AGENT ? <Users className="h-4 w-4 text-secondary-foreground" /> : <UserCheck className="h-4 w-4 text-muted-foreground" />}
                        <span>{u.username}</span>
                        {u.hasChildren && <Badge variant="outline" className="text-xs ml-2">
                            {users.filter(user => user.parentId === u.id).length}
                          </Badge>}
                      </div>
                    </div>
                  </TableCell>
                  {/* ── Description Cell ─────────────────────────────────── */}
                  <TableCell className="min-w-[160px] max-w-[220px] border-r border-amber-200 dark:border-amber-800/60">
                    {editingDescriptionId === u.id ? (
                      <div className="flex items-center gap-1">
                        <Input
                          autoFocus
                          value={descriptionDraft}
                          onChange={e => setDescriptionDraft(e.target.value)}
                          onKeyDown={e => {
                            if (e.key === 'Enter') saveDescription(u.id);
                            if (e.key === 'Escape') cancelEditDescription();
                          }}
                          className="h-7 text-xs px-2 flex-1 min-w-0"
                          placeholder="Add description…"
                          disabled={savingDescriptionId === u.id}
                        />
                        <Button
                          variant="ghost" size="sm"
                          className="h-7 w-7 p-0 text-green-600 hover:text-green-700 hover:bg-green-50 flex-shrink-0"
                          onClick={() => saveDescription(u.id)}
                          disabled={savingDescriptionId === u.id}
                        >
                          <Check className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          variant="ghost" size="sm"
                          className="h-7 w-7 p-0 text-destructive hover:text-destructive hover:bg-destructive/10 flex-shrink-0"
                          onClick={cancelEditDescription}
                          disabled={savingDescriptionId === u.id}
                        >
                          <XIcon className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1 group">
                        <span className={cn("text-xs truncate flex-1", u.description ? 'text-foreground' : 'text-muted-foreground/40')}>
                          {u.description || '—'}
                        </span>
                        <Button
                          variant="ghost" size="sm"
                          className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                          onClick={() => startEditDescription(u.id, u.description)}
                        >
                          <Pencil className="h-3 w-3 text-muted-foreground" />
                        </Button>
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground border-r border-amber-200 dark:border-amber-800/60">
                    {u.nickname ? <span className="font-medium text-foreground">{u.nickname}</span> : <span className="text-muted-foreground/50">—</span>}
                  </TableCell>
                  <TableCell className="border-r border-orange-200 dark:border-orange-800/60">
                    <Badge variant={u.tier === UserTier.MASTER ? 'default' : u.tier === UserTier.COMPANY ? 'default' : u.tier === UserTier.AGENT ? 'secondary' : 'outline'}
                      className={u.tier === UserTier.MASTER ? 'bg-purple-600 text-white' : ''}>
                      {u.tier}
                    </Badge>
                  </TableCell>
                  {/* Commission cell — stored as ticketPrice (0.00–1.00); commission% = (1 - ticketPrice) * 100 */}
                  <TableCell className="font-mono border-r border-orange-200 dark:border-orange-800/60">{((1 - (u.ticketPrice ?? 1)) * 100).toFixed(0)}%</TableCell>
                  {/* ── Fight On/Off Cell ──────────────────────────────────── */}
                  {/* Company: read-only view of downline's OWN blanket fight On/Off   */}
                  {/* Agent:   editable — shows the agent's fight against each child   */}
                  <TableCell className="min-w-[90px] border-r border-blue-200 dark:border-blue-800/60">
                    {(() => {
                      const viewerIsCompany = user?.tier === UserTier.COMPANY || user?.tier === UserTier.MASTER;

                      // ── Company viewer: read-only ──────────────────────────────────────────
                      if (viewerIsCompany) {
                        if (u.tier === UserTier.PLAYER) {
                          // Show the parent agent's individual fight on/off against this player
                          if (!u.parentId) return <span className="text-xs text-muted-foreground/50">—</span>;
                          const fs = getFightSetting(u.parentId, u.id);
                          return (
                            <span className={cn('text-xs font-medium', fs.fightOnOff ? 'text-green-600' : 'text-muted-foreground')}>
                              {fs.fightOnOff ? 'On' : 'Off'}
                            </span>
                          );
                        }
                        // Agent/Company rows: show agent's own blanket fight on/off
                        const blanket = getBlanketFightSetting(u.id);
                        const isOn = blanket.fightOnOff;
                        return (
                          <span className={cn('text-xs font-medium', isOn ? 'text-green-600' : 'text-muted-foreground')}>
                            {isOn ? 'On' : 'Off'} <span className="text-[10px] text-muted-foreground font-normal">(blanket)</span>
                          </span>
                        );
                      }

                      // ── Agent viewer: editable ─────────────────────────────────────────────
                      const isOwnRow = user?.tier === UserTier.AGENT && u.id === user?.id;
                      const isDirectChild = u.parentId === user?.id;

                      if (isOwnRow) {
                        // Own row: reads the sentinel row (parent_id = child_id = user.id)
                        // Independent of individual child rows — toggling a child never changes this
                        const blanket = getBlanketFightSetting(user!.id);
                        const isSaving = savingFightOnOff.has(`blanket_${user?.id}`);
                        return (
                          <div className="flex flex-col gap-0.5">
                            <div className="flex items-center gap-1">
                              <Switch
                                checked={blanket.fightOnOff}
                                onCheckedChange={val => handleFightOnOff(user!.id, val)}
                                disabled={isSaving}
                                className="scale-75"
                              />
                              <span className={cn("text-xs font-medium", blanket.fightOnOff ? "text-green-600" : "text-muted-foreground")}>
                                {blanket.fightOnOff ? 'On' : 'Off'}
                              </span>
                            </div>
                            <span className="text-[10px] text-muted-foreground leading-tight">blanket</span>
                          </div>
                        );
                      }

                      if (isDirectChild) {
                        // Direct child: show the parent's fight on/off against THIS child
                        const fs = getFightSetting(user!.id, u.id);
                        const isSaving = savingFightOnOff.has(`${user?.id}:${u.id}`);
                        return (
                          <div className="flex items-center gap-1">
                            <Switch
                              checked={fs.fightOnOff}
                              onCheckedChange={val => handleFightOnOff(u.id, val)}
                              disabled={isSaving}
                              className="scale-75"
                            />
                            <span className={cn("text-xs font-medium", fs.fightOnOff ? "text-green-600" : "text-muted-foreground")}>
                              {fs.fightOnOff ? 'On' : 'Off'}
                            </span>
                          </div>
                        );
                      }

                      // Non-direct rows: read-only (viewer has no relationship with this user)
                      return <span className="text-xs text-muted-foreground">—</span>;
                    })()}
                  </TableCell>
                  {/* ── Fight % Cell ─────────────────────────────────────────── */}
                  {/* Company: read-only view of downline's OWN blanket fight%       */}
                  {/* Agent:   editable — shows the agent's fight% against each child */}
                  <TableCell className="font-mono min-w-[110px] border-r border-blue-200 dark:border-blue-800/60">
                    {(() => {
                      const viewerIsCompany2 = user?.tier === UserTier.COMPANY || user?.tier === UserTier.MASTER;

                      // ── Company viewer: read-only fight% ──────────────────────────────────
                      if (viewerIsCompany2) {
                        if (u.tier === UserTier.PLAYER) {
                          // Show the parent agent's individual fight% set against this player
                          if (!u.parentId) return <span className="text-muted-foreground/50">—</span>;
                          const fs = getFightSetting(u.parentId, u.id);
                          return (
                            <span className={cn('text-xs', fs.fightOnOff ? 'text-foreground font-semibold' : 'text-muted-foreground')}>
                              {fs.fightPct}%
                            </span>
                          );
                        }
                        // Agent/Company rows: show agent's own blanket fight%
                        const blanket = getBlanketFightSetting(u.id);
                        const isOn = blanket.fightOnOff;
                        const pct = isOn ? (blanket.fightPct ?? u.fightPercentage ?? 0) : 0;
                        return (
                          <span className={cn('text-xs', isOn ? 'text-foreground font-semibold' : 'text-muted-foreground')}>
                            {pct}% <span className="text-[10px] text-muted-foreground font-normal">(blanket)</span>
                          </span>
                        );
                      }

                      // ── Agent viewer: editable fight% ─────────────────────────────────────
                      const isOwnRow = user?.tier === UserTier.AGENT && u.id === user?.id;
                      const currentUserRow = users.find(usr => usr.id === user?.id);
                      const parentIsIndividual = currentUserRow?.fightPctMode === 'multiple';
                      const isDirectChildOfAgent = user?.tier === UserTier.AGENT && u.parentId === user?.id;

                      if (isOwnRow) {
                        // Own row: choose blanket mode (Individual / 0% / 5%...)
                        // Enabled only when blanket sentinel is ON
                        const ownLimit = user?.fightPctLimit ?? 0;
                        const currentMode = currentUserRow?.fightPctMode ?? 'single';
                        const currentPct = u.fightPercentage ?? 0;
                        // Use sentinel row to determine if fight is on (not individual children)
                        const blanketOn = getBlanketFightSetting(user!.id).fightOnOff;
                        const displayPct = blanketOn ? currentPct : 0;
                        const selectVal = currentMode === 'multiple' ? 'individual' : String(displayPct);
                        const isSaving = savingFightPct.has(u.id);
                        // Dropdown: 0% up to own fight% limit (no cap — deduction applied per child when displayed)
                        return (
                          <Select
                            value={selectVal}
                            onValueChange={v => handleOwnFightPctModeChange(v === 'individual' ? 'multiple' : v)}
                            disabled={isSaving || !blanketOn}
                          >
                            <SelectTrigger className="h-7 text-xs w-full">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="individual">Individual</SelectItem>
                              {Array.from({ length: ownLimit + 1 }, (_, i) => i).map(v => (
                                <SelectItem key={v} value={String(v)}>{v}%</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        );
                      } else if (isDirectChildOfAgent && parentIsIndividual) {
                        // Child row in Individual mode: per-child fight% dropdown
                        const fs = getFightSetting(user!.id, u.id);
                        const fightEnabled = fs.fightOnOff;
                        const parentLimit = user?.fightPctLimit ?? 0;
                        const childLimit = u.fightPctLimit ?? 0;
                        // Individual: max = parentLimit - childLimit (same cap as blanket)
                        const hardMax = Math.max(0, parentLimit - childLimit);
                        const isSaving = savingFightPct.has(u.id);
                        const options = getIndividualFightPctOptions(hardMax);
                        return (
                          <Select
                            value={String(fs.fightPct)}
                            onValueChange={val => handleChildFightPctChange(u.id, parseInt(val))}
                            disabled={isSaving || !fightEnabled}
                          >
                            <SelectTrigger className="h-7 text-xs w-full">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {options.map(v => (
                                <SelectItem key={v} value={String(v)}>{v}%</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        );
                      } else if (isDirectChildOfAgent && !parentIsIndividual) {
                        // Blanket single mode: show the relationship fight%
                        const fs = getFightSetting(user!.id, u.id);
                        const fightEnabled = fs.fightOnOff;
                        return <span className={!fightEnabled ? 'text-muted-foreground' : ''}>{fs.fightPct}%</span>;
                      } else {
                        // Read-only for non-direct rows
                        return <span className="text-muted-foreground">—</span>;
                      }
                    })()}
                  </TableCell>
                  <TableCell className="font-mono text-muted-foreground border-r border-blue-200 dark:border-blue-800/60">
                    {u.tier === UserTier.PLAYER ? <span className="text-muted-foreground/50">—</span> : `${u.fightPctLimit ?? (u.tier === UserTier.AGENT ? u.fightPercentage : 100)}%`}
                  </TableCell>
                  {/* ── Credit Allocation Cell ── */}
                  <TableCell className="font-mono border-r border-green-200 dark:border-green-800/60">
                    {(() => {
                      const allocated = users.filter(user => user.parentId === u.id).reduce((sum, user) => sum + (user.creditLimit || 0), 0);
                      const available = (u.creditLimit || 0) - allocated;
                      const percentage = u.creditLimit ? (allocated / u.creditLimit * 100).toFixed(0) : 0;
                      return <div className="space-y-1">
                        <div className="flex items-center gap-2 text-xs">
                          <span className="text-amber-600 font-semibold">{formatCurrency(allocated)}</span>
                          <span className="text-muted-foreground">/</span>
                          <span className="text-muted-foreground">{formatCurrency(u.creditLimit || 0)}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                            <div className="h-full bg-amber-500 transition-all" style={{ width: `${percentage}%` }} />
                          </div>
                        </div>
                        <div className="text-xs text-green-600 font-medium">
                          {t.userMgmt.available}: {formatCurrency(available)}
                        </div>
                      </div>;
                    })()}
                  </TableCell>
                  {/* ── Credit Refresh Rate Cell ── */}
                  <TableCell className="min-w-[180px] border-r border-green-200 dark:border-green-800/60">
                    {(() => {
                      // Only the direct parent can manage a child's refresh rate
                      const isDirectChild = u.parentId === user?.id;
                      if (!isDirectChild) {
                        // Read-only display for non-direct children or own row
                        const rate = u.creditRefreshRate ?? 'manual';
                        const labels: Record<string, string> = { auto_daily: 'Auto – Daily (3:30 PM)', auto_weekly: 'Auto – Weekly (Mon 3:30 PM)', manual: 'Manual' };
                        return <span className="text-xs text-muted-foreground">{labels[rate] ?? rate}</span>;
                      }
                      const rate = u.creditRefreshRate ?? 'manual';
                      const isSaving = savingRefreshRate.has(u.id);
                      const isRefreshing = manualRefreshing.has(u.id);
                      return (
                        <div className="flex items-center gap-2 flex-wrap">
                          <Select
                            value={rate}
                            onValueChange={(val) => handleSetRefreshRate(u.id, val as 'auto_daily' | 'auto_weekly' | 'manual')}
                            disabled={isSaving}
                          >
                            <SelectTrigger className="h-7 text-xs w-[130px]">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="manual"><span className="flex items-center gap-1"><RefreshCw className="h-3 w-3" />Manual</span></SelectItem>
                              <SelectItem value="auto_daily"><span className="flex items-center gap-1"><Clock className="h-3 w-3" />Auto – Daily (3:30 PM daily)</span></SelectItem>
                              <SelectItem value="auto_weekly"><span className="flex items-center gap-1"><Calendar className="h-3 w-3" />Auto – Weekly (Mon 3:30 PM)</span></SelectItem>
                            </SelectContent>
                          </Select>
                          {rate === 'manual' && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-7 text-xs px-2"
                              disabled={isRefreshing}
                              onClick={() => handleManualRefresh(u.id, u.nickname || u.username)}
                            >
                              {isRefreshing
                                ? <RefreshCw className="h-3 w-3 animate-spin" />
                                : <RefreshCw className="h-3 w-3" />}
                              <span className="ml-1">{isRefreshing ? 'Refreshing…' : 'Refresh'}</span>
                            </Button>
                          )}
                        </div>
                      );
                    })()}
                  </TableCell>
                  <TableCell className="text-right border-r border-slate-200 dark:border-slate-700/60">
                    {u.id === user?.id ? (
                      /* Own account — keep the 3-dot dropdown */
                      <UserActionsDropdown targetUser={u} onUpdate={handleUpdateUser} onStatusChange={toggleStatus} allUsers={users} onRefresh={() => loadUsers(false)} onEditProfile={openEditPage} />
                    ) : (
                      /* Other users — show Edit Profile button */
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8 text-xs gap-1"
                        onClick={() => openEditPage(u)}
                      >
                        <Pencil className="h-3 w-3" /> Edit Profile
                      </Button>
                    )}
                  </TableCell>
                </TableRow>)}
          </TableBody>
        </Table>
        </div>}

      {/* ── User Log Section ─────────────────────────────────────────────── */}
      <UserLogPanel users={users} currentUserId={user?.id || ''} currentUserTier={user?.tier} />
    </div>;
}
// ── CreateUserPage ───────────────────────────────────────────────────────────
// Full-page user creation form (replaces the old popup dialog)
function CreateUserPage({
  users,
  onCreated,
  onBack,
}: {
  users: User[];
  onCreated: (userData: any) => Promise<void>;
  onBack: () => void;
}) {
  const {
    user,
    isMaster,
    isCompany,
    isAgent
  } = useAuth();
  const { t } = useLanguage();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  const availableCredit = useMemo(() => {
    if (!user) return 0;
    const allocated = users.filter(u => u.parentId === user.id).reduce((sum, u) => sum + (u.creditLimit || 0), 0);
    return (user.creditLimit || 0) - allocated;
  }, [user, users]);

  const creatorMinPrice = (isCompany() || isMaster()) ? 0.00 : (user?.ticketPrice ?? 0.01);
  const [formData, setFormData] = useState({
    username: '',
    description: '',
    nickname: '',
    password: '',
    tier: '' as UserTier,
    creditLimit: 0,
    creditRefreshRate: 'manual' as 'manual' | 'auto_daily' | 'auto_weekly',
    rebate: 0,
    fightPercentage: 0,
    ticketPrice: creatorMinPrice,
  });
  const fightRange = useMemo(() => {
    if (!formData.tier || formData.tier === UserTier.PLAYER) return { min: 0, max: 0 };
    if (formData.tier === UserTier.COMPANY) return { min: 100, max: 100 };
    return getFightPercentageRange(formData.tier, user?.fightPercentage, true, user?.fightPctLimit);
  }, [formData.tier, user?.fightPercentage, user?.fightPctLimit]);

  const priceRange = useMemo(() => {
    if (!formData.tier) return { min: 0, max: 0 };
    return getTicketPriceRange(user?.tier || UserTier.COMPANY, user?.ticketPrice, true);
  }, [formData.tier, user?.ticketPrice, user?.tier]);

  // Rebate options: 0% to creator's commission in 1% steps
  const creatorCommission = Math.round((1 - (user?.ticketPrice ?? 1)) * 100);
  const rebateOptions = useMemo(() => {
    const max = creatorCommission;
    return Array.from({ length: max + 1 }, (_, i) => i);
  }, [creatorCommission]);

  const availableTiers = useMemo(() => {
    if (isMaster()) return [UserTier.COMPANY, UserTier.AGENT, UserTier.PLAYER];
    if (isCompany()) return [UserTier.COMPANY, UserTier.AGENT, UserTier.PLAYER];
    if (isAgent()) return [UserTier.AGENT, UserTier.PLAYER];
    return [];
  }, [isMaster, isCompany, isAgent]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.username || !formData.password || !formData.tier) return;
    setSubmitError(null);
    setIsSubmitting(true);
    try {
      const effectiveLimit = user?.fightPctLimit ?? user?.fightPercentage ?? 100;
      if (!validateFightPercentage(formData.tier, formData.fightPercentage, effectiveLimit)) {
        const maxAllowed = Math.max(0, effectiveLimit - 5);
        setSubmitError(formData.tier === UserTier.COMPANY ? 'Company fight % must be 100%.' : formData.tier === UserTier.PLAYER ? 'Players do not have a fight %.' : `Agent fight % must be 0\u2013${maxAllowed}% in steps of 5%.`);
        setIsSubmitting(false);
        return;
      }
      if (formData.creditLimit > availableCredit) {
        setSubmitError(`Insufficient credit. You have ${formatCurrency(availableCredit)} available.`);
        setIsSubmitting(false);
        return;
      }
      const finalTicketPrice = 1 - formData.rebate / 100;
      if (!validateTicketPrice(user?.tier || UserTier.COMPANY, finalTicketPrice, user?.ticketPrice)) {
        setSubmitError(`Invalid rebate. Valid range: 0%\u2013${creatorCommission}%.`);
        setIsSubmitting(false);
        return;
      }
      await onCreated({
        username: formData.username,
        description: formData.description.trim() || undefined,
        nickname: formData.nickname.trim() || undefined,
        password: formData.password,
        tier: formData.tier,
        creditLimit: formData.creditLimit,
        creditRefreshRate: formData.creditRefreshRate,
        ticketPrice: finalTicketPrice,
        fightPercentage: formData.fightPercentage,
      });
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to create user');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isCompany() && !isAgent()) return null;

  return (
    <div className="max-w-2xl mx-auto py-6 px-4 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={onBack} className="gap-1">
          <ChevronLeft className="h-4 w-4" /> Back
        </Button>
        <div>
          <h2 className="text-2xl font-bold tracking-tight">{t.userMgmt.createNewUser}</h2>
          <p className="text-muted-foreground text-sm">{t.userMgmt.createDesc}</p>
        </div>
      </div>

      <Card>
        <CardContent className="pt-6">
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* 1. Username */}
            <div className="space-y-1.5">
              <Label htmlFor="cu-username">{t.userMgmt.colUsername} <span className="text-destructive">*</span></Label>
              <Input id="cu-username" value={formData.username} onChange={e => setFormData(p => ({ ...p, username: e.target.value }))} required autoFocus />
            </div>

            {/* 2. Description */}
            <div className="space-y-1.5">
              <Label htmlFor="cu-description">Description</Label>
              <Input id="cu-description" value={formData.description} onChange={e => setFormData(p => ({ ...p, description: e.target.value }))} placeholder="Optional notes about this user" />
            </div>

            {/* 3. Nickname */}
            <div className="space-y-1.5">
              <Label htmlFor="cu-nickname">{t.userMgmt.nicknameOptional}</Label>
              <Input id="cu-nickname" value={formData.nickname} placeholder={t.userMgmt.nicknameReceipt} onChange={e => setFormData(p => ({ ...p, nickname: e.target.value }))} />
              <p className="text-xs text-muted-foreground">Appears on receipts. Can be set by the user on first login.</p>
            </div>

            {/* 4. Password */}
            <div className="space-y-1.5">
              <Label htmlFor="cu-password">{t.userMgmt.initialPassword} <span className="text-destructive">*</span></Label>
              <div className="relative">
                <Input id="cu-password" type={showPassword ? 'text' : 'password'} value={formData.password} onChange={e => setFormData(p => ({ ...p, password: e.target.value }))} required className="pr-9" />
                <button type="button" tabIndex={-1} onClick={() => setShowPassword(v => !v)} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {/* 5. Account Level */}
            <div className="space-y-1.5">
              <Label htmlFor="cu-tier">{t.userMgmt.accountTier} <span className="text-destructive">*</span></Label>
              <Select value={formData.tier} onValueChange={val => {
                const newTier = val as UserTier;
                const newMin = getTicketPriceRange(user?.tier || UserTier.COMPANY, user?.ticketPrice, true).min;
                setFormData(p => ({ ...p, tier: newTier, fightPercentage: newTier === UserTier.COMPANY ? 100 : newTier === UserTier.PLAYER ? 0 : p.fightPercentage, ticketPrice: newMin }));
              }}>
                <SelectTrigger id="cu-tier"><SelectValue placeholder={t.userMgmt.selectTier} /></SelectTrigger>
                <SelectContent>{availableTiers.map(tier => <SelectItem key={tier} value={tier}>{tier === UserTier.AGENT ? 'Agent' : tier === UserTier.PLAYER ? 'Player' : tier}</SelectItem>)}</SelectContent>
              </Select>
            </div>

            {/* 6. Credit Limit */}
            <div className="space-y-1.5">
              <Label htmlFor="cu-credit">{t.userMgmt.creditLimit}</Label>
              <Input id="cu-credit" type="number" min="0" max={availableCredit} step="1"
                value={formData.creditLimit === 0 ? '' : formData.creditLimit}
                placeholder="0"
                onChange={e => setFormData(p => ({ ...p, creditLimit: parseFloat(e.target.value) || 0 }))} />
              <p className="text-xs text-muted-foreground">{t.userMgmt.available}: {formatCurrency(availableCredit)}</p>
            </div>

            {/* 7. Credit Refresh Rate */}
            <div className="space-y-1.5">
              <Label htmlFor="cu-refresh">Credit Refresh Rate</Label>
              <Select value={formData.creditRefreshRate} onValueChange={val => setFormData(p => ({ ...p, creditRefreshRate: val as 'manual' | 'auto_daily' | 'auto_weekly' }))}>
                <SelectTrigger id="cu-refresh"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="manual">Manual</SelectItem>
                  <SelectItem value="auto_daily">Auto Daily (3:30 PM MYT)</SelectItem>
                  <SelectItem value="auto_weekly">Auto Weekly (Mon 3:30 PM MYT)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* 8. Rebate (Commission) */}
            <div className="space-y-1.5">
              <Label htmlFor="cu-rebate">Rebate <span className="text-xs text-muted-foreground">(0% - {creatorCommission}%)</span></Label>
              <Select value={String(formData.rebate)} onValueChange={val => setFormData(p => ({ ...p, rebate: parseInt(val), ticketPrice: 1 - parseInt(val) / 100 }))}>
                <SelectTrigger id="cu-rebate"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {rebateOptions.map(v => <SelectItem key={v} value={String(v)}>{v}%</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">Your own rebate: {creatorCommission}%. Default: 0%.</p>
            </div>

            {/* 9. Fight % Limit */}
            {(formData.tier === UserTier.AGENT || formData.tier === UserTier.COMPANY) && (
              <div className="space-y-1.5">
                <Label htmlFor="cu-fight">
                  {t.userMgmt.fightPct} limit
                  {formData.tier === UserTier.AGENT && <span className="text-xs text-muted-foreground ml-1">(0% - {fightRange.max}%)</span>}
                </Label>
                {formData.tier === UserTier.COMPANY ? (
                  <><Input id="cu-fight" value="100%" disabled /><p className="text-xs text-muted-foreground">{t.userMgmt.companyFixedFight}</p></>
                ) : (
                  <Select value={String(formData.fightPercentage)} onValueChange={val => setFormData(p => ({ ...p, fightPercentage: parseInt(val) }))}>
                    <SelectTrigger id="cu-fight"><SelectValue placeholder={t.userMgmt.selectFightPct} /></SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: Math.floor(fightRange.max / 5) + 1 }, (_, i) => i * 5).filter(v => v >= 0 && v <= fightRange.max).map(v => <SelectItem key={v} value={String(v)}>{v}%</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
              </div>
            )}

            {submitError && (
              <div className="bg-destructive/10 border border-destructive/20 rounded-md p-3 text-sm text-destructive">
                {submitError}
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" onClick={onBack} className="flex-1">Back</Button>
              <Button type="submit" disabled={isSubmitting} className="flex-1">
                {isSubmitting ? 'Creating\u2026' : t.userMgmt.createAccount}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

// \u2500\u2500 EditProfilePage \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500
function EditProfilePage({
  targetUser,
  onUpdate,
  onStatusChange,
  onBack,
}: {
  targetUser: User;
  onUpdate: (userId: string, updates: any) => Promise<void>;
  onStatusChange: (user: User, status: UserStatus) => Promise<void>;
  onBack: () => void;
}) {
  const { user, isMaster, isCompany, isAgent } = useAuth();
  const { t } = useLanguage();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [statusChanging, setStatusChanging] = useState(false);

  const [formData, setFormData] = useState({
    description: targetUser.description ?? '',
    nickname: targetUser.nickname ?? '',
    password: '',
    creditLimit: targetUser.creditLimit,
    creditRefreshRate: (targetUser.creditRefreshRate ?? 'manual') as 'manual' | 'auto_daily' | 'auto_weekly',
    rebate: Math.round((1 - (targetUser.ticketPrice ?? 1)) * 100),
    fightPctLimit: targetUser.fightPctLimit ?? 0,
  });

  const creatorCommission = Math.round((1 - (user?.ticketPrice ?? 1)) * 100);

  const rebateOptions = useMemo(() => {
    const max = creatorCommission;
    return Array.from({ length: max + 1 }, (_, i) => i);
  }, [creatorCommission]);

  const fightLimitOptions = useMemo(() => {
    const myLimit = user?.fightPctLimit ?? 100;
    const max = Math.max(0, myLimit - 5);
    return Array.from({ length: max + 1 }, (_, i) => i);
  }, [user?.fightPctLimit]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    setIsSubmitting(true);
    try {
      const updates: Record<string, unknown> = {
        description: formData.description.trim(),
        nickname: formData.nickname.trim() || null,
        creditLimit: formData.creditLimit,
        creditRefreshRate: formData.creditRefreshRate,
        ticketPrice: 1 - formData.rebate / 100,
        fightPctLimit: formData.fightPctLimit,
      };
      if (formData.password.trim()) {
        updates.password = formData.password.trim();
      }
      await onUpdate(targetUser.id, updates);
      toast.success(`Profile updated for ${targetUser.username}`);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to update profile');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto py-6 px-4 space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={onBack} className="gap-1">
          <ChevronLeft className="h-4 w-4" /> Back
        </Button>
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Edit Profile</h2>
          <p className="text-muted-foreground text-sm">Editing profile for <strong>{targetUser.username}</strong></p>
        </div>
      </div>

      <Card>
        <CardContent className="pt-6">
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Username \u2014 read-only */}
            <div className="space-y-1.5">
              <Label>{t.userMgmt.colUsername}</Label>
              <Input value={targetUser.username} disabled className="opacity-60 cursor-not-allowed" />
              <p className="text-xs text-muted-foreground">Username cannot be changed.</p>
            </div>

            {/* Description */}
            <div className="space-y-1.5">
              <Label htmlFor="ep-description">Description</Label>
              <Input id="ep-description" value={formData.description} onChange={e => setFormData(p => ({ ...p, description: e.target.value }))} placeholder="Optional notes" />
            </div>

            {/* Nickname */}
            <div className="space-y-1.5">
              <Label htmlFor="ep-nickname">{t.userMgmt.nicknameOptional}</Label>
              <Input id="ep-nickname" value={formData.nickname} placeholder={t.userMgmt.nicknameReceipt} onChange={e => setFormData(p => ({ ...p, nickname: e.target.value }))} />
            </div>

            {/* Password */}
            <div className="space-y-1.5">
              <Label htmlFor="ep-password">New Password <span className="text-xs text-muted-foreground">(leave blank to keep current)</span></Label>
              <div className="relative">
                <Input id="ep-password" type={showPassword ? 'text' : 'password'} value={formData.password} onChange={e => setFormData(p => ({ ...p, password: e.target.value }))} className="pr-9" placeholder="Enter new password or leave blank" />
                <button type="button" tabIndex={-1} onClick={() => setShowPassword(v => !v)} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {/* Account Level \u2014 read-only */}
            <div className="space-y-1.5">
              <Label>{t.userMgmt.accountTier}</Label>
              <Input value={targetUser.tier} disabled className="opacity-60 cursor-not-allowed" />
            </div>

            {/* Credit Limit */}
            <div className="space-y-1.5">
              <Label htmlFor="ep-credit">{t.userMgmt.creditLimit}</Label>
              <Input id="ep-credit" type="number" min="0" step="1"
                value={formData.creditLimit === 0 ? '' : formData.creditLimit}
                placeholder="0"
                onChange={e => setFormData(p => ({ ...p, creditLimit: parseFloat(e.target.value) || 0 }))} />
            </div>

            {/* Credit Refresh Rate */}
            <div className="space-y-1.5">
              <Label htmlFor="ep-refresh">Credit Refresh Rate</Label>
              <Select value={formData.creditRefreshRate} onValueChange={val => setFormData(p => ({ ...p, creditRefreshRate: val as 'manual' | 'auto_daily' | 'auto_weekly' }))}>
                <SelectTrigger id="ep-refresh"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="manual">Manual</SelectItem>
                  <SelectItem value="auto_daily">Auto Daily (3:30 PM MYT)</SelectItem>
                  <SelectItem value="auto_weekly">Auto Weekly (Mon 3:30 PM MYT)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Rebate (Commission) */}
            {(isMaster() || isCompany() || (isAgent() && targetUser.parentId === user?.id)) && (
              <div className="space-y-1.5">
                <Label htmlFor="ep-rebate">Rebate <span className="text-xs text-muted-foreground">(0% - {creatorCommission}%)</span></Label>
                <Select value={String(formData.rebate)} onValueChange={val => setFormData(p => ({ ...p, rebate: parseInt(val) }))}>
                  <SelectTrigger id="ep-rebate"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {rebateOptions.map(v => <SelectItem key={v} value={String(v)}>{v}%</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Fight % Limit */}
            {targetUser.tier === UserTier.AGENT && (isMaster() || isCompany() || (isAgent() && targetUser.parentId === user?.id)) && (
              <div className="space-y-1.5">
                <Label htmlFor="ep-fight">{t.userMgmt.fightPct} Limit <span className="text-xs text-muted-foreground">(0% - {Math.max(0, (user?.fightPctLimit ?? 100) - 5)}%)</span></Label>
                <Select value={String(formData.fightPctLimit)} onValueChange={val => setFormData(p => ({ ...p, fightPctLimit: parseInt(val) }))}>
                  <SelectTrigger id="ep-fight"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {fightLimitOptions.map(v => <SelectItem key={v} value={String(v)}>{v}%</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}

            {submitError && (
              <div className="bg-destructive/10 border border-destructive/20 rounded-md p-3 text-sm text-destructive">
                {submitError}
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" onClick={onBack} className="flex-1">Back</Button>
              <Button type="submit" disabled={isSubmitting} className="flex-1">
                {isSubmitting ? 'Saving…' : 'Save Changes'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Account Status Section */}
      <Card className="border-border">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Account Status</CardTitle>
          <CardDescription>
            Current status: <strong className={targetUser.status === UserStatus.ACTIVE ? 'text-primary' : targetUser.status === UserStatus.SUSPENDED ? 'text-amber-500' : 'text-destructive'}>{targetUser.status}</strong>
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {targetUser.status !== UserStatus.ACTIVE && (
              <Button
                variant="outline"
                size="sm"
                className="text-primary border-primary hover:bg-primary/10"
                disabled={statusChanging}
                onClick={async () => {
                  setStatusChanging(true);
                  try { await onStatusChange(targetUser, UserStatus.ACTIVE); onBack(); }
                  catch { toast.error('Failed to activate account'); }
                  finally { setStatusChanging(false); }
                }}
              >
                <ShieldOff className="mr-2 h-4 w-4" /> {t.userMgmt.activateAccount}
              </Button>
            )}
            {targetUser.status !== UserStatus.SUSPENDED && (
              <Button
                variant="outline"
                size="sm"
                className="text-amber-500 border-amber-500 hover:bg-amber-50"
                disabled={statusChanging}
                onClick={async () => {
                  setStatusChanging(true);
                  try { await onStatusChange(targetUser, UserStatus.SUSPENDED); onBack(); }
                  catch { toast.error('Failed to suspend account'); }
                  finally { setStatusChanging(false); }
                }}
              >
                <ShieldAlert className="mr-2 h-4 w-4" /> {t.userMgmt.suspendAccount}
              </Button>
            )}
            {targetUser.status !== UserStatus.BANNED && (
              <Button
                variant="outline"
                size="sm"
                className="text-destructive border-destructive hover:bg-destructive/10"
                disabled={statusChanging}
                onClick={async () => {
                  setStatusChanging(true);
                  try { await onStatusChange(targetUser, UserStatus.BANNED); onBack(); }
                  catch { toast.error('Failed to ban account'); }
                  finally { setStatusChanging(false); }
                }}
              >
                <ShieldAlert className="mr-2 h-4 w-4" /> {t.userMgmt.banAccount}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
// ── LimitTableDialog ─────────────────────────────────────────────────────────
const HOME_SCORES  = ['1-0','2-0','2-1','3-0','3-1','3-2','4-0','4-1','4-2','4-3'];
const AWAY_SCORES  = ['0-1','0-2','1-2','0-3','1-3','2-3','0-4','1-4','2-4','3-4'];
const DRAW_SCORES  = ['0-0','1-1','2-2','3-3','4-4'];
const ALL_SCORE_KEYS = [...HOME_SCORES, ...AWAY_SCORES, ...DRAW_SCORES, 'AOS'];

const DEFAULT_LIMIT_COMPANY = 50000;
const DEFAULT_LIMIT_AGENT  = 50000;
// Players have no own per-score limit — they share their agent's pool

function getDefaultLimit(tier: UserTier, fightPct?: number): number {
  if (tier === UserTier.MASTER || tier === UserTier.COMPANY) return DEFAULT_LIMIT_COMPANY;
  if (tier === UserTier.AGENT) {
    // Formula: 50000 / (1 - fightPct) where fightPct is 0–1
    // At 0%: 50,000  |  At 50%: 100,000  |  At 80%: 250,000  |  At 95%: 1,000,000
    const pct = (fightPct ?? 0) / 100;
    if (pct <= 0) return DEFAULT_LIMIT_AGENT;
    if (pct >= 1) return DEFAULT_LIMIT_AGENT * 20;
    return Math.round(DEFAULT_LIMIT_AGENT / (1 - pct));
  }
  // PLAYER — no own limit (enforced via agent's pool)
  return 0;
}

function LimitTableDialog({ targetUser, onClose, readOnly = false }: { targetUser: User; onClose: () => void; readOnly?: boolean }) {
  const { t } = useLanguage();
  const { user } = useAuth();
  const { matches } = useBetting();

  const [selectedMatch, setSelectedMatch] = useState<Match | null>(null);
  const [limits, setLimits] = useState<Record<string, string>>({});
  // ownUsed: bets already placed (fight-adjusted for company, raw for agent)
  const [ownUsed, setOwnUsed] = useState<Record<string, number>>({});
  // ancestorMinLimits: tightest upline limit per score {limit, tier}
  const [ancestorMinLimits, setAncestorMinLimits] = useState<Record<string, { limit: number; tier: string }>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [applyAllValue, setApplyAllValue] = useState<string>('');
  // isCompanyOrMaster: true when viewing own limit as company/master
  const [isCompanyOrMaster, setIsCompanyOrMaster] = useState(false);

  const sortedMatches = useMemo(() =>
    [...matches].sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime()),
  [matches]);

  // Load limits + hierarchy view using the new limit_view action
  const loadLimitsForMatch = useCallback(async (match: Match) => {
    setIsLoading(true);
    try {
      // limit_view returns: ownLimits, ownUsed, ancestorMinLimits
      const { data: viewData } = await supabase.functions.invoke(
        'score_limits_2026_05_12',
        { body: { action: 'limit_view', userId: targetUser.id, matchId: match.id } }
      );

      const saved: Record<string, number> = viewData?.ownLimits || {};
      const used: Record<string, number> = viewData?.ownUsed || {};
      const ancestors: Record<string, { limit: number; tier: string }> = viewData?.ancestorMinLimits || {};

      setOwnUsed(used);
      setAncestorMinLimits(ancestors);
      setIsCompanyOrMaster(viewData?.isCompanyOrMaster ?? false);

      // Build input state — default to tier-based if no saved value
      // Players have no own limit (0 = inherits from agent)
      const defaultVal = getDefaultLimit(targetUser.tier, targetUser.fightPercentage);
      const newLimits: Record<string, string> = {};
      ALL_SCORE_KEYS.forEach(k => {
        newLimits[k] = (saved[k] != null && saved[k] > 0) ? String(saved[k]) : (defaultVal > 0 ? String(defaultVal) : '');
      });
      setLimits(newLimits);
    } catch (e) {
      console.error('Failed to load limits', e);
    } finally {
      setIsLoading(false);
    }
  }, [targetUser.id, targetUser.tier]);

  const handleSelectMatch = (match: Match) => {
    setSelectedMatch(match);
    loadLimitsForMatch(match);
  };

  const handleSave = async () => {
    if (!selectedMatch) return;
    setIsSaving(true);
    try {
      const limitsToSave: Record<string, number> = {};
      ALL_SCORE_KEYS.forEach(k => {
        const v = parseFloat(limits[k] || '0');
        limitsToSave[k] = isNaN(v) ? 0 : v;
      });
      // The edge function 'set' action automatically clamps to upline caps server-side
      const { error } = await supabase.functions.invoke(
        'score_limits_2026_05_12',
        { body: { action: 'set', user_id: targetUser.id, match_id: selectedMatch.id, limits: limitsToSave } }
      );
      if (error) throw error;
      toast.success('Limits saved successfully');
      // Reload to reflect server-clamped values
      loadLimitsForMatch(selectedMatch);
    } catch (e: any) {
      toast.error('Failed to save limits: ' + (e.message || 'Unknown error'));
    } finally {
      setIsSaving(false);
    }
  };

  // Tightest upline cap for a score (if any)
  const getUplineCap = (scoreKey: string): number => {
    const a = ancestorMinLimits[scoreKey];
    return a && a.limit > 0 ? a.limit : Number.MAX_SAFE_INTEGER;
  };

  const setLimit = (scoreKey: string, value: string) => {
    const cap = getUplineCap(scoreKey);
    const num = parseFloat(value);
    if (!isNaN(num) && cap !== Number.MAX_SAFE_INTEGER && num > cap) {
      setLimits(prev => ({ ...prev, [scoreKey]: String(cap) }));
      toast.warning(`Limit capped at ${cap.toLocaleString()} (${ancestorMinLimits[scoreKey]?.tier ?? 'upline'} limit)`);
      return;
    }
    setLimits(prev => ({ ...prev, [scoreKey]: value }));
  };

  const handleApplyAll = () => {
    const raw = parseFloat(applyAllValue);
    if (isNaN(raw) || raw < 0) { toast.error('Please enter a valid non-negative number'); return; }
    const newLimits: Record<string, string> = {};
    ALL_SCORE_KEYS.forEach(k => {
      const cap = getUplineCap(k);
      newLimits[k] = String(cap !== Number.MAX_SAFE_INTEGER ? Math.min(raw, cap) : raw);
    });
    setLimits(newLimits);
    toast.success(`Applied ${raw.toLocaleString()} to all scores (capped to upline limits where applicable)`);
  };

  // renderScoreRow: plain function to avoid re-mounting on keystrokes
  const isPlayer = targetUser.tier === UserTier.PLAYER;
  const renderScoreRow = (scoreKey: string, odds?: number, label?: string) => {
    const display = label || scoreKey;
    const used = ownUsed[scoreKey] || 0;
    const limitVal = parseFloat(limits[scoreKey] || '0') || 0;
    const remaining = limitVal > 0 ? Math.max(0, limitVal - used) : null;
    const isFull = limitVal > 0 && used >= limitVal;
    const cap = getUplineCap(scoreKey);
    const hasUplineCap = cap !== Number.MAX_SAFE_INTEGER;
    // Check if any ancestor company limit is hit for this score
    const ancestorInfo = ancestorMinLimits[scoreKey] as { limit: number; tier: string; remaining?: number } | undefined;
    const companyLimitReached = ancestorInfo &&
      (ancestorInfo.tier?.toUpperCase() === 'COMPANY' || ancestorInfo.tier?.toUpperCase() === 'MASTER') &&
      (ancestorInfo.remaining ?? 1) <= 0;
    return (
      <tr key={scoreKey} className="border-b border-gray-100 hover:bg-gray-50">
        <td className="px-3 py-1.5 text-sm font-semibold text-center">{display}</td>
        <td className="px-3 py-1.5 text-sm text-center text-blue-700 font-mono">
          {odds != null ? odds.toFixed(2) : '–'}
        </td>
        <td className="px-3 py-1.5 text-sm text-center font-mono">
          <span className={used > 0 ? 'text-amber-700 font-semibold' : 'text-muted-foreground'}>
            {used > 0 ? used.toFixed(2) : '0.00'}
          </span>
        </td>
        <td className="px-3 py-1.5 text-center">
          {isPlayer ? (
            // Players have no own limit — they share their agent's pool
            <div className="w-28 h-7 flex items-center justify-center text-xs font-mono rounded border border-gray-200 bg-gray-50 text-muted-foreground italic">
              Agent limit
            </div>
          ) : readOnly ? (
            <div className={`w-28 h-7 flex items-center justify-center text-sm font-mono rounded border ${
              isFull ? 'border-red-300 bg-red-50 text-red-600 font-bold' : 'border-gray-200 bg-gray-50 text-foreground'
            }`}>
              {limitVal > 0 ? limitVal.toLocaleString() : '–'}
            </div>
          ) : (
            <input
              type="number"
              min="0"
              max={hasUplineCap ? cap : undefined}
              step="1"
              placeholder={hasUplineCap ? `0–${cap.toLocaleString()}` : 'No limit'}
              value={limits[scoreKey] ?? ''}
              onChange={e => setLimit(scoreKey, e.target.value)}
              className={`w-28 h-7 text-sm text-center font-mono border rounded px-2 focus:outline-none focus:ring-2 focus:ring-primary/50 ${
                isFull ? 'border-red-400 text-red-600' : 'border-input bg-background'
              }`}
            />
          )}
        </td>
        <td className="px-3 py-1.5 text-sm text-center font-mono">
          {isPlayer ? (
            <span className="text-muted-foreground text-xs italic">via agent</span>
          ) : companyLimitReached ? (
            // Company quota exhausted — agent still has own balance but blanket blocked
            <span className="text-red-600 font-bold text-xs">Company Limit Reached</span>
          ) : remaining !== null ? (
            <span className={remaining <= 0 ? 'text-red-600 font-bold' : 'text-green-600'}>
              {remaining.toFixed(2)}
            </span>
          ) : <span className="text-muted-foreground">–</span>}
        </td>
      </tr>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-background">
      {/* Header */}
      <div className="flex items-center gap-3 px-6 py-4 border-b bg-card shadow-sm">
        <LayoutList className="h-5 w-5 text-primary shrink-0" />
        <div className="flex-1 text-center">
          <h2 className="font-bold text-base leading-tight">{t.userMgmt.limitTable}</h2>
          <p className="text-xs text-muted-foreground">
            {targetUser.username} — {selectedMatch
              ? `${selectedMatch.homeTeam} vs ${selectedMatch.awayTeam}`
              : t.userMgmt.selectMatch}
          </p>
        </div>
        {selectedMatch && !readOnly && (
          <Button className="shrink-0" size="sm" onClick={handleSave} disabled={isSaving}>
            <Save className="h-4 w-4 mr-1" />
            {isSaving ? t.userMgmt.saving : t.userMgmt.saveLimits}
          </Button>
        )}
        {selectedMatch && readOnly && (
          <span className="shrink-0 text-xs bg-amber-100 text-amber-700 border border-amber-300 rounded-full px-3 py-1 font-semibold">{t.userMgmt.viewOnly}</span>
        )}
        {!selectedMatch && <div className="w-5 shrink-0" />}
      </div>

      {/* Body */}
      {/* Back button row — always visible below the header */}
      <div className="px-6 py-2 border-b bg-muted/30">
        <Button variant="ghost" size="sm" onClick={selectedMatch ? () => setSelectedMatch(null) : onClose}
          className="flex items-center gap-1 text-muted-foreground hover:text-foreground">
          <ChevronLeft className="h-4 w-4" />
          {selectedMatch ? t.userMgmt.backToMatches : t.userMgmt.close}
        </Button>
      </div>

      <div className="flex-1 overflow-auto p-6">
        {!selectedMatch ? (
          // ── Match selection ───────────────────────────────────────────────
          <div className="max-w-2xl mx-auto space-y-3">
            <p className="text-sm text-muted-foreground mb-4">
              {readOnly
                ? <>{t.userMgmt.viewLimitsFor} <strong>{targetUser.username}</strong>.</>
                : <>{t.userMgmt.selectMatchForLimits} <strong>{targetUser.username}</strong> {t.userMgmt.andDownline}.</>}
            </p>
            {sortedMatches.length === 0 && (
              <div className="text-center text-muted-foreground py-12">{t.userMgmt.noMatches}</div>
            )}
            {sortedMatches.map(match => (
              <button
                key={match.id}
                onClick={() => handleSelectMatch(match)}
                className="w-full flex items-center justify-between p-4 rounded-xl border bg-card hover:border-primary hover:shadow-md transition-all text-left group"
              >
                <div>
                  <p className="font-semibold text-sm group-hover:text-primary">
                    {match.homeTeam} <span className="text-muted-foreground font-normal">vs</span> {match.awayTeam}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {new Date(match.startTime).toLocaleString()}
                  </p>
                </div>
                <Badge variant="outline" className={match.status === 'UPCOMING' ? 'border-green-500 text-green-600' : ''}>
                  {match.status}
                </Badge>
              </button>
            ))}
          </div>
        ) : (
          // ── Score limit table ─────────────────────────────────────────────
          <div className="max-w-3xl mx-auto">
            {isLoading ? (
              <div className="flex items-center justify-center py-16">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
              </div>
            ) : (
              <div className="space-y-6">
                <p className="text-xs text-muted-foreground">
                  {readOnly
                    ? <>{t.userMgmt.currentLimitsFor} <strong>{targetUser.username}</strong>. {t.userMgmt.contactUpline}</>
                    : <>{t.userMgmt.setMaxBetDesc} <strong>{targetUser.username}</strong> {t.userMgmt.setMaxBetDescEnd}</>
                  }
                </p>

                {/* ── Set To All Scores – hidden in read-only mode ──────── */}
                {!readOnly && (
                  <div className="flex items-center gap-3 p-4 bg-muted/40 border rounded-xl">
                    <div className="flex-1">
                      <label className="block text-xs font-semibold text-foreground mb-1">{t.userMgmt.setToAllScores}</label>
                      <p className="text-xs text-muted-foreground">{t.userMgmt.setToAllScoresDesc}</p>
                    </div>
                    <input
                      type="number"
                      min="0"
                      step="1"
                      placeholder={`e.g. ${getDefaultLimit(targetUser.tier).toLocaleString()}`}
                      value={applyAllValue}
                      onChange={e => setApplyAllValue(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && handleApplyAll()}
                      className="w-32 h-9 text-sm text-center font-mono border border-input bg-background rounded px-2 focus:outline-none focus:ring-2 focus:ring-primary/50"
                    />
                    <Button size="sm" onClick={handleApplyAll} className="h-9 px-4">
                      {t.userMgmt.apply}
                    </Button>
                  </div>
                )}

                {/* Column headers */}
                <div className="rounded-xl border overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-muted/60">
                      <tr>
                        <th className="px-3 py-2 text-xs font-semibold text-center">{t.userMgmt.score}</th>
                        <th className="px-3 py-2 text-xs font-semibold text-center">{t.userMgmt.odds}</th>
                        <th className="px-3 py-2 text-xs font-semibold text-center">
                          {isCompanyOrMaster ? 'Bets Placed After Fight' : t.userMgmt.betsPlaced}
                        </th>
                        <th className="px-3 py-2 text-xs font-semibold text-center">{t.userMgmt.limit}</th>
                        <th className="px-3 py-2 text-xs font-semibold text-center">{t.userMgmt.remaining}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {/* Home Wins */}
                      <tr className="bg-blue-50">
                        <td colSpan={5} className="px-3 py-1 text-xs font-bold text-blue-800">🏠 Home Win</td>
                      </tr>
                      {HOME_SCORES.map(s => renderScoreRow(s, selectedMatch.scoreOdds?.[s]))}
                      {/* Away Wins */}
                      <tr className="bg-orange-50">
                        <td colSpan={5} className="px-3 py-1 text-xs font-bold text-orange-800">✈️ Away Win</td>
                      </tr>
                      {AWAY_SCORES.map(s => renderScoreRow(s, selectedMatch.scoreOdds?.[s]))}
                      {/* Draw */}
                      <tr className="bg-green-50">
                        <td colSpan={5} className="px-3 py-1 text-xs font-bold text-green-800">🤝 Draw</td>
                      </tr>
                      {DRAW_SCORES.map(s => renderScoreRow(s, selectedMatch.scoreOdds?.[s]))}
                      {/* AOS */}
                      <tr className="bg-purple-50">
                        <td colSpan={5} className="px-3 py-1 text-xs font-bold text-purple-800">🎲 Any Other Score (AOS)</td>
                      </tr>
                      {renderScoreRow('AOS', selectedMatch.scoreOdds?.['AOS'], 'AOS')}
                    </tbody>
                  </table>
                </div>

                {!readOnly && (
                  <div className="flex justify-end">
                    <Button onClick={handleSave} disabled={isSaving} className="min-w-32">
                      <Save className="h-4 w-4 mr-2" />
                      {isSaving ? t.userMgmt.saving : t.userMgmt.saveAllLimits}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export function UserActionsDropdown({
  targetUser,
  onUpdate,
  onStatusChange,
  allUsers,
  onRefresh,
  onEditProfile,
}: {
  targetUser: User;
  onUpdate: (userId: string, updates: any) => Promise<void>;
  onStatusChange: (user: User, status: UserStatus) => Promise<void>;
  allUsers: User[];
  onRefresh: () => Promise<void>;
  onEditProfile?: (u: User) => void;
}) {
  const {
    user,
    canManageUser,
    isMaster,
    isCompany,
    isAgent,
    canSetFightPercentage,
    refreshUser,
    patchUser
  } = useAuth();
  const { t } = useLanguage();
  const [dialogMode, setDialogMode] = useState<'password' | 'balance' | 'credit_limit' | 'fight_percentage' | 'fight_pct_history' | 'fight_pct_limit' | 'ticket_price' | 'nickname' | null>(null);
  const [showLimitTable, setShowLimitTable] = useState(false);
  const [tempValue, setTempValue] = useState('');
  const [fightPctHistory, setFightPctHistory] = useState<Array<{id: string; old_pct: number; new_pct: number; changed_at: string; note?: string; targetUsername?: string; changedByUsername?: string}>>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Load fight% history for the target user when the fight_percentage dialog opens
  const loadFightHistory = async () => {
    if (!targetUser?.id) return;
    setLoadingHistory(true);
    try {
      const { data } = await supabase.functions.invoke('fight_pct_mgmt_2026_05_19', {
        body: { action: 'get_history', userId: targetUser.id },
      });
      if (data?.success) setFightPctHistory(data.history || []); // newest first (edge fn orders desc)
    } catch (e) { /* ignore */ }
    finally { setLoadingHistory(false); }
  };

  // Count all descendants recursively
  const countDescendants = useCallback((userId: string): number => {
    const children = allUsers.filter(u => u.parentId === userId);
    let count = children.length;
    children.forEach(child => {
      count += countDescendants(child.id);
    });
    return count;
  }, [allUsers]);
  // Guard: if targetUser is somehow undefined (e.g. mid-render after update), bail out safely
  const targetUserId = targetUser?.id ?? '';
  const descendantsCount = useMemo(() => targetUserId ? countDescendants(targetUserId) : 0, [targetUserId, countDescendants]);
  if (!targetUser || !canManageUser(targetUser)) return null;
  const handleAction = async () => {
    // Capture all values from props/state NOW before any await,
    // so they are safe even if the component re-renders mid-flight.
    const capturedMode = dialogMode;
    const capturedValue = tempValue;
    const capturedUserId = targetUser.id;
    const capturedUsername = targetUser.username;

    // Close dialog immediately so the component is no longer "active"
    // and a subsequent re-render won't trip over stale prop references.
    setDialogMode(null);
    setTempValue('');

    try {
      if (capturedMode === 'password') {
        await onUpdate(capturedUserId, { password: capturedValue });
        toast.success(`Password changed for ${capturedUsername}`);
      } else if (capturedMode === 'balance') {
        await onUpdate(capturedUserId, { balance: parseFloat(capturedValue) });
        toast.success(`Balance updated for ${capturedUsername}`);
      } else if (capturedMode === 'credit_limit') {
        await onUpdate(capturedUserId, { creditLimit: parseFloat(capturedValue) });
        toast.success(`Credit limit updated for ${capturedUsername}`);
      } else if (capturedMode === 'fight_percentage') {
        // Route through fight_pct_mgmt edge function to record timestamped history
        const newPct = parseInt(capturedValue);
        const { data, error: fnError } = await supabase.functions.invoke('fight_pct_mgmt_2026_05_19', {
          body: {
            action: 'set_fight_pct',
            targetUserId: capturedUserId,
            changedBy: user?.id,
            newPct,
          },
        });
        if (fnError || !data?.success) {
          throw new Error(data?.error || fnError?.message || 'Failed to update fight %');
        }
        toast.success(`Fight % updated for ${capturedUsername} (change recorded with timestamp)`);
        // Update the users list entry (covers both self and downline).
        // For self-edit, hierarchicalUsers now reads from the `users` list first,
        // so setUsers triggers an immediate re-render of the agent's own row.
        await onUpdate(capturedUserId, { fightPercentage: newPct });
        // Reload history panel
        await loadFightHistory();
      } else if (capturedMode === 'fight_pct_limit') {
        // Update the downline's fight_pct_limit via the standard update action
        await onUpdate(capturedUserId, { fightPctLimit: parseInt(capturedValue) });
        toast.success(`Fight % Limit set to ${capturedValue}% for ${capturedUsername}`);
      } else if (capturedMode === 'ticket_price') {
        // tempValue is entered as commission% (0–100); ticketPrice = 1 - commission/100
        const commissionPct = parseFloat(capturedValue);
        const ticketPriceDecimal = 1 - commissionPct / 100;
        await onUpdate(capturedUserId, { ticketPrice: ticketPriceDecimal });
        toast.success(`Commission updated to ${commissionPct}% for ${capturedUsername}`);
      } else if (capturedMode === 'nickname') {
        await onUpdate(capturedUserId, { nickname: capturedValue.trim() || null });
        toast.success(`Nickname set for ${capturedUsername}`);
      }
      // One controlled silent refresh after the dialog is already closed
      await onRefresh();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to update user');
    }
  };
  // Determine Limit Table read-only logic:
  // - Agent viewing their own: read-only (they can only view, not self-edit)
  // - Agent viewing direct child (agent or player): editable
  // - Company/Master viewing anyone (including own): editable
  const limitTableReadOnly = isAgent() && targetUser.id === user?.id;

  if (showLimitTable) {
    return <LimitTableDialog
      targetUser={targetUser}
      onClose={() => setShowLimitTable(false)}
      readOnly={limitTableReadOnly}
    />;
  }
  return <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="h-8 w-8 p-0">
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel>{t.userMgmt.actionsFor}: {targetUser.username}</DropdownMenuLabel>
          <DropdownMenuSeparator />

          {/* Edit Profile */}
          {onEditProfile && targetUser.id !== user?.id && (
            <DropdownMenuItem onClick={() => onEditProfile(targetUser)} className="font-medium">
              <Pencil className="mr-2 h-4 w-4" /> Edit Profile
            </DropdownMenuItem>
          )}
          {onEditProfile && targetUser.id !== user?.id && <DropdownMenuSeparator />}

          {/* Limit Table: only for own account */}
          {targetUser.id === user?.id && (
            <DropdownMenuItem onClick={() => setShowLimitTable(true)}>
              <LayoutList className="mr-2 h-4 w-4" />
              {t.userMgmt.limitTable}{limitTableReadOnly ? ` (${t.userMgmt.view})` : ''}
            </DropdownMenuItem>
          )}

          {/* Change Password: only for own account */}
          {targetUser.id === user?.id && (
            <DropdownMenuItem onClick={() => setDialogMode('password')}>
              <Lock className="mr-2 h-4 w-4" /> {t.userMgmt.changePassword}
            </DropdownMenuItem>
          )}

          {/* Set Nickname — one-time option; hidden once a nickname is already set */}
          {!targetUser.nickname && (
            <DropdownMenuItem onClick={() => { setTempValue(''); setDialogMode('nickname'); }}>
              <Tag className="mr-2 h-4 w-4" /> {t.userMgmt.setNickname}
            </DropdownMenuItem>
          )}

          {/* Agents can view fight % history for their OWN account via dialog */}
          {isAgent() && targetUser.id === user?.id &&
            <DropdownMenuItem onClick={() => { setDialogMode('fight_pct_history'); loadFightHistory(); }}>
              <Percent className="mr-2 h-4 w-4" /> {t.userMgmt.viewFightHistory}
            </DropdownMenuItem>}

          {/* Actions NOT available for own account */}
          {targetUser.id !== user?.id && <>
            {/* View Fight % History — company/master can view any downline's history */}
            {(isMaster() || isCompany()) &&
              <DropdownMenuItem onClick={() => { setDialogMode('fight_pct_history'); loadFightHistory(); }}>
                <Percent className="mr-2 h-4 w-4" /> {t.userMgmt.viewFightHistory}
              </DropdownMenuItem>}

            {(isMaster() || isCompany()) && <DropdownMenuItem onClick={() => { setTempValue(String(((1 - (targetUser.ticketPrice ?? 1)) * 100).toFixed(0))); setDialogMode('ticket_price'); }}>
                <Ticket className="mr-2 h-4 w-4" /> {t.userMgmt.editCommission}
              </DropdownMenuItem>}

            {/* Status actions moved to Edit Profile page */}
          </>}

        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={dialogMode !== null} onOpenChange={o => !o && setDialogMode(null)}>
        <DialogContent className={(dialogMode === 'fight_percentage' || dialogMode === 'fight_pct_limit' || dialogMode === 'fight_pct_history') ? 'sm:max-w-lg' : ''}>
          <DialogHeader>
            <DialogTitle>
              {dialogMode === 'password' && t.userMgmt.changePassword}
              {dialogMode === 'balance' && t.userMgmt.adjustBalance}
              {dialogMode === 'credit_limit' && t.userMgmt.editCreditLimit}
              {dialogMode === 'fight_percentage' && t.userMgmt.editFightPct}
              {dialogMode === 'fight_pct_history' && t.userMgmt.viewFightHistory}
              {dialogMode === 'fight_pct_limit' && t.userMgmt.fightLimit}
              {dialogMode === 'ticket_price' && t.userMgmt.editCommission}
              {dialogMode === 'nickname' && t.userMgmt.setNickname}
            </DialogTitle>
            <DialogDescription>
              <span>{t.userMgmt.updateFor} <strong>{targetUser.username}</strong></span>
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-3">
            {/* ── History-only view — no edit controls ── */}
            {dialogMode === 'fight_pct_history' ? (
              <div className="space-y-2">
                {loadingHistory ? (
                  <p className="text-xs text-muted-foreground">Loading...</p>
                ) : fightPctHistory.length === 0 ? (
                  <p className="text-xs text-muted-foreground italic">No changes recorded yet.</p>
                ) : (
                  <div className="max-h-72 overflow-y-auto rounded border bg-muted/30 divide-y text-xs">
                    {fightPctHistory.map(h => (
                      <div key={h.id} className="px-2 py-1.5 space-y-0.5">
                        <div className="flex items-center justify-between">
                          <span className="font-mono text-muted-foreground text-[10px]">
                            {new Date(h.changed_at).toLocaleString()}
                          </span>
                          <span>
                            <span className="text-red-500">{h.old_pct}%</span>
                            <span className="mx-1 text-muted-foreground">→</span>
                            <span className="text-green-600 font-semibold">{h.new_pct}%</span>
                          </span>
                        </div>
                        <div className="text-[10px] text-muted-foreground">
                          {(() => {
                            const note = h.note ?? '';
                            if (note === 'single_all') {
                              return <span>📢 <strong>{h.changedByUsername}</strong> set <strong>{h.new_pct}%</strong> against all</span>;
                            }
                            if (note.startsWith('multiple_mode:')) {
                              const childId = note.split(':')[1];
                              const childUser = allUsers.find(u => u.id === childId);
                              const childName = childUser ? (childUser.nickname || childUser.username) : childId;
                              return <span>🎯 <strong>{h.changedByUsername}</strong> set <strong>{h.new_pct}%</strong> against <strong>{childName}</strong></span>;
                            }
                            return <span>✏️ <strong>{h.changedByUsername}</strong> set own fight % to <strong>{h.new_pct}%</strong></span>;
                          })()}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
            <>
            <Label htmlFor="temp-val">
              {dialogMode === 'password' && t.userMgmt.newPassword}
              {dialogMode === 'balance' && t.userMgmt.newBalance}
              {dialogMode === 'credit_limit' && t.userMgmt.newCreditLimit}
              {dialogMode === 'fight_percentage' && (targetUser.id === user?.id ? 'New Fight % (My Own)' : t.userMgmt.newFightPct)}
              {dialogMode === 'fight_pct_limit' && t.userMgmt.fightPctLimitLabel}
              {dialogMode === 'ticket_price' && t.userMgmt.commissionPct}
              {dialogMode === 'nickname' && t.userMgmt.nicknameOptional}
            </Label>
            {(dialogMode === 'fight_percentage' || dialogMode === 'fight_pct_limit') ? (() => {
              const isEditingSelf = dialogMode === 'fight_percentage' && targetUser.id === user?.id;
              const setterLimit = user?.fightPctLimit ?? 100;
              // Hard ceiling: self = own limit, downline = setterLimit - 5
              const hardMax = isEditingSelf ? setterLimit : Math.max(0, setterLimit - 5);
              const maxChoice = hardMax;
              const currentVal = dialogMode === 'fight_percentage'
                ? (targetUser.fightPercentage ?? 0)
                : (targetUser.fightPctLimit ?? (targetUser.tier === UserTier.AGENT ? targetUser.fightPercentage : 100));
              return (
                <Select value={tempValue || String(currentVal)} onValueChange={val => setTempValue(val)}>
                  <SelectTrigger id="temp-val">
                    <SelectValue placeholder={dialogMode === 'fight_pct_limit' ? 'Select limit' : t.userMgmt.selectFightPct} />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: maxChoice + 1 }, (_, i) => i).map(v => (
                      <SelectItem key={v} value={v.toString()}>
                        {v}% {v === currentVal ? '(current)' : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              );
            })() : dialogMode === 'nickname' ? (
              <>
                <Input id="temp-val" type="text" value={tempValue} onChange={e => setTempValue(e.target.value)} placeholder="Enter display name for receipts" autoFocus />
                <p className="text-xs text-muted-foreground">This nickname will appear on receipts instead of the username. It can only be set once.</p>
              </>
            ) : dialogMode === 'ticket_price' ? (
              <>
                {/* commission% = (1 - ticketPrice) * 100
                    parent's commission determines the MAX that can be set for child:
                    child commission must be >= 0 and <= parent's commission */}
                <div className="flex items-center gap-1">
                  <Input
                    id="temp-val"
                    type="number"
                    value={tempValue}
                    onChange={e => setTempValue(e.target.value)}
                    min={0}
                    max={(() => {
                      const parentTP = allUsers.find(u => u.id === targetUser.parentId)?.ticketPrice ?? 0;
                      // parent commission = (1 - parentTP)*100; child can be set up to that
                      return Math.round((1 - parentTP) * 100);
                    })()}
                    step="1"
                    autoFocus
                  />
                  <span className="text-sm text-muted-foreground">%</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {(() => {
                    const parentTP = allUsers.find(u => u.id === targetUser.parentId)?.ticketPrice ?? 0;
                    const maxPct = Math.round((1 - parentTP) * 100);
                    const currentPct = Math.round((1 - (targetUser.ticketPrice ?? 1)) * 100);
                    return `0% – ${maxPct}% in steps of 1% · Current: ${currentPct}%`;
                  })()}
                </p>
              </>
            ) : (
              <Input id="temp-val" type={dialogMode === 'password' ? 'text' : 'number'} value={tempValue} onChange={e => setTempValue(e.target.value)} placeholder={dialogMode === 'password' ? t.userMgmt.enterPassword : '0.00'} step="0.01" autoFocus />
            )}
            {dialogMode === 'fight_pct_limit' && (
              <>
                <p className="text-xs text-muted-foreground">
                  {`0% to ${Math.max(0, (user?.fightPctLimit ?? 100) - 5)}% in steps of 1% · Current limit for ${targetUser.username}: ${targetUser.fightPctLimit ?? targetUser.fightPercentage ?? 0}%`}
                </p>
                <p className="text-xs text-amber-600 font-medium">⚠️ This caps the max fight % <strong>{targetUser.username}</strong> can assign to their downlines, and the ceiling for their own fight %.</p>
              </>
            )}
            {dialogMode === 'fight_percentage' && (
              <>
                <p className="text-xs text-muted-foreground">
                  {targetUser.id === user?.id
                    ? `0% to ${user?.fightPctLimit ?? 100}% in steps of 1% · Current: ${targetUser.fightPercentage ?? 0}% (your assigned limit: ${user?.fightPctLimit ?? 100}%)`
                    : `0% to ${Math.max(0, (user?.fightPctLimit ?? 100) - 5)}% in steps of 1% · Current: ${targetUser.fightPercentage ?? 0}%`}
                </p>
                <p className="text-xs text-amber-600 font-medium">⏰ This change will be timestamped. Bets placed after the change will use the new fight %.</p>
              </>
            )}
            {dialogMode === 'credit_limit' && <p className="text-xs text-muted-foreground mt-1">
                {t.userMgmt.setCreditLimitNote}
              </p>}
            </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogMode(null)}>
              {dialogMode === 'fight_pct_history' ? 'Close' : t.userMgmt.cancel}
            </Button>
            {dialogMode !== 'fight_pct_history' && (
              <Button variant="default" onClick={handleAction}>
                {t.userMgmt.saveChanges}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>;
}
// ── User Log Panel ────────────────────────────────────────────────────────────
interface UserLogEntry {
  id: string;
  user_id: string;
  username: string;
  logged_in_at: string;
  logged_out_at: string | null;
  ip_address: string | null;
  duration_seconds: number | null;
}

function formatDuration(seconds: number | null): string {
  if (seconds === null || seconds === undefined) return '—';
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  if (mins < 60) return `${mins}m ${secs}s`;
  const hrs = Math.floor(mins / 60);
  const remMins = mins % 60;
  return `${hrs}h ${remMins}m`;
}

function UserLogPanel({ users, currentUserId, currentUserTier }: {
  users: User[];
  currentUserId: string;
  currentUserTier?: string;
}) {
  const { t } = useLanguage();
  const [logs, setLogs] = useState<UserLogEntry[]>([]);
  const [logLoading, setLogLoading] = useState(false);
  const [usernameFilter, setUsernameFilter] = useState<string>('__ALL__');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Usernames visible to current user
  const visibleUsernames = useMemo(() => {
    if (!currentUserId) return [];
    const getDescendants = (uid: string): User[] => {
      const direct = users.filter(u => u.parentId === uid);
      return direct.concat(direct.flatMap(d => getDescendants(d.id)));
    };
    const self = users.find(u => u.id === currentUserId);
    const all = self ? [self, ...getDescendants(currentUserId)] : getDescendants(currentUserId);
    return all.map(u => u.username);
  }, [users, currentUserId]);

  const fetchLogs = useCallback(async () => {
    setLogLoading(true);
    try {
      const { data } = await supabase.functions.invoke('user_logs_2026_05_13', {
        body: {
          action: 'fetch',
          usernameFilter: usernameFilter === '__ALL__' ? '' : usernameFilter,
          dateFrom: dateFrom || undefined,
          dateTo: dateTo || undefined,
          visibleUsernames
        }
      });
      if (data?.logs) setLogs(data.logs);
    } catch (_) {
      // silent
    } finally {
      setLogLoading(false);
    }
  }, [usernameFilter, dateFrom, dateTo, visibleUsernames]);

  // Auto-fetch on mount and when filters change
  useEffect(() => {
    if (visibleUsernames.length > 0) fetchLogs();
  }, [fetchLogs]);

  return (
    <div className="space-y-4 mt-8">
      <div>
        <h2 className="text-xl font-bold tracking-tight">{t.userMgmt.userLog}</h2>
        <p className="text-muted-foreground text-sm">{t.userMgmt.userLogSubtitle}</p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-end">
        {/* Username dropdown */}
        <div>
          <Select value={usernameFilter} onValueChange={setUsernameFilter}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder={t.userMgmt.filterByUsername} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__ALL__">All Users</SelectItem>
              {visibleUsernames.map(name => (
                <SelectItem key={name} value={name}>{name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-xs text-muted-foreground whitespace-nowrap">{t.userMgmt.from}</Label>
          <Input
            type="date"
            className="w-36 text-sm"
            value={dateFrom}
            onChange={e => setDateFrom(e.target.value)}
          />
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-xs text-muted-foreground whitespace-nowrap">{t.userMgmt.to}</Label>
          <Input
            type="date"
            className="w-36 text-sm"
            value={dateTo}
            min={dateFrom || undefined}
            onChange={e => setDateTo(e.target.value)}
          />
        </div>
        <Button size="sm" variant="outline" onClick={fetchLogs} disabled={logLoading}>
          {logLoading ? t.userMgmt.loading : t.userMgmt.search}
        </Button>
        <Button size="sm" variant="ghost" onClick={() => { setUsernameFilter('__ALL__'); setDateFrom(''); setDateTo(''); }}>
          {t.userMgmt.clear}
        </Button>
      </div>

      {/* Table */}
      <div className="border rounded-xl bg-card overflow-hidden">
        <Table>
          <TableHeader className="bg-muted/50">
            <TableRow>
              <TableHead>{t.userMgmt.colUsername}</TableHead>
              <TableHead>{t.userMgmt.logInTime}</TableHead>
              <TableHead>{t.userMgmt.logOutTime}</TableHead>
              <TableHead>{t.userMgmt.ipAddress}</TableHead>
              <TableHead>{t.userMgmt.duration}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {logLoading ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                  {t.userMgmt.loadingLogs}
                </TableCell>
              </TableRow>
            ) : logs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                  {t.userMgmt.noLogs}
                </TableCell>
              </TableRow>
            ) : logs.map(log => (
              <TableRow key={log.id}>
                <TableCell className="font-medium">{log.username}</TableCell>
                <TableCell className="text-sm font-mono">
                  {new Date(log.logged_in_at).toLocaleString()}
                </TableCell>
                <TableCell className="text-sm font-mono">
                  {log.logged_out_at ? new Date(log.logged_out_at).toLocaleString() : (
                    <span className="text-green-600 font-semibold">{t.userMgmt.active}</span>
                  )}
                </TableCell>
                <TableCell className="font-mono text-sm">{log.ip_address || '—'}</TableCell>
                <TableCell className="font-mono text-sm">{formatDuration(log.duration_seconds)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}